import * as Google from 'expo-auth-session/providers/google';
import { Platform } from 'react-native';

import { googleConfig } from '@/src/config/env';
import type { GoogleCredentialTokens } from '@/src/features/auth/auth-service';
import { UserFacingError } from '@/src/utils/errors';

const fallbackGoogleClientId =
  '000000000000-placeholder.apps.googleusercontent.com';

type GoogleUserInfo = {
  email?: unknown;
  name?: unknown;
  picture?: unknown;
};

export type GooglePrefillProfile = GoogleCredentialTokens & {
  email?: string;
  fullName?: string;
  photoURL?: string;
};

function cleanProfileString(value: unknown, maxLength: number) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return undefined;
  }

  return trimmed.slice(0, maxLength);
}

function configuredClientId() {
  return Platform.select({
    android: googleConfig.androidClientId,
    ios: googleConfig.iosClientId,
    default: googleConfig.webClientId,
  });
}

export async function fetchGooglePrefillProfile(
  tokens: GoogleCredentialTokens,
): Promise<GooglePrefillProfile> {
  if (!tokens.accessToken) {
    return {
      accessToken: tokens.accessToken ?? null,
      idToken: tokens.idToken ?? null,
    };
  }

  const response = await fetch(
    'https://openidconnect.googleapis.com/v1/userinfo',
    {
      headers: {
        Authorization: `Bearer ${tokens.accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error('Google profile details were unavailable.');
  }

  const profile = (await response.json()) as GoogleUserInfo;

  return {
    accessToken: tokens.accessToken ?? null,
    email: cleanProfileString(profile.email, 254),
    fullName: cleanProfileString(profile.name, 80),
    idToken: tokens.idToken ?? null,
    photoURL: cleanProfileString(profile.picture, 2048),
  };
}

export function useGoogleAuthRequest() {
  const clientId = configuredClientId();
  const hasAuthSessionClient = Boolean(clientId);
  const providerEnabled = googleConfig.enabled;
  const usesFirebasePopup = Platform.OS === 'web';
  const available =
    providerEnabled && (usesFirebasePopup || hasAuthSessionClient);
  const [request, , promptAsync] = Google.useIdTokenAuthRequest({
    androidClientId: googleConfig.androidClientId || fallbackGoogleClientId,
    iosClientId: googleConfig.iosClientId || fallbackGoogleClientId,
    selectAccount: true,
    webClientId: googleConfig.webClientId || fallbackGoogleClientId,
  });

  async function promptForCredentialTokens() {
    if (!providerEnabled) {
      throw new UserFacingError(
        'Google sign-in is not enabled for Frienzzi yet.',
      );
    }

    if (usesFirebasePopup) {
      return null;
    }

    if (!hasAuthSessionClient) {
      throw new UserFacingError(
        'Google sign-in is not configured for this app yet.',
      );
    }

    const result = await promptAsync();

    if (result.type !== 'success') {
      throw new UserFacingError('Google sign-in was canceled.');
    }

    const tokens = {
      accessToken:
        result.authentication?.accessToken ??
        ('params' in result ? result.params.access_token : undefined) ??
        null,
      idToken:
        result.authentication?.idToken ??
        ('params' in result ? result.params.id_token : undefined) ??
        null,
    };

    if (!tokens.idToken && !tokens.accessToken) {
      throw new Error('Google did not return a sign-in token.');
    }

    return tokens;
  }

  async function promptForPrefillProfile() {
    const tokens = await promptForCredentialTokens();

    if (!tokens) {
      return null;
    }

    return fetchGooglePrefillProfile(tokens);
  }

  return {
    disabled:
      !available ||
      (!usesFirebasePopup && (!hasAuthSessionClient || !request)),
    enabled: providerEnabled,
    available,
    fetchPrefillProfile: fetchGooglePrefillProfile,
    promptForCredentialTokens,
    promptForPrefillProfile,
  };
}
